// =========================================================
// * Vue Material Dashboard PRO - v1.4.0
// =========================================================
//
// * Product Page: https://www.creative-tim.com/product/vue-material-dashboard-pro
// * Copyright 2019 Creative Tim (https://www.creative-tim.com)
//
// * Coded by Creative Tim
//
// =========================================================
//
// * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

import Vue from "vue";
import axios from "axios";

import { indexedDBName, indexedDBVersion } from './sw-vars'

const openIndexedDB = () => {
    return new Promise((resolve, reject) => {
        const idbRequest = indexedDB.open(indexedDBName, indexedDBVersion)

        // Event when a new version of the database is available
        idbRequest.onupgradeneeded = (event) => {
            let db = idbRequest.result

            // Create stores in indexedDB
            if(!db.objectStoreNames.contains('data')) {
                db.createObjectStore('data', { keyPath: 'key', autoIncrement: true })
            }
            if(!db.objectStoreNames.contains('requests')) {
                db.createObjectStore('requests', { keyPath: 'key', autoIncrement: true })
            }
            if(!db.objectStoreNames.contains('failed_requests')) {
                db.createObjectStore('failed_requests', { keyPath: 'key', autoIncrement: true })
            }

            resolve(idbRequest)
        }

        // Event when database is opened successfully
        idbRequest.onsuccess = (event) => {
            resolve(idbRequest)
        }
    })
}

// ServiceWorker
if('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/service-worker.js')
        .then(async (registration) => {
            // SW Registered

            if(registration.waiting) {
                // New SW is waiting - auto update failed
                localStorage.setItem('pwa-version-reload', JSON.stringify(false))
                localStorage.setItem('pwa-cache-reload', JSON.stringify(true))
                registration.waiting.postMessage('SKIP_WAITING');

                // Await IndexedDB setup (version, stores)
                await openIndexedDB()
                window.location.reload()
            }

            registration.addEventListener('updatefound', async () => {
                // SW Updated

                const newWorker = registration.installing
                newWorker.addEventListener('statechange', async () => {
                    // After skipWaiting(), detect when new Service Worker is "activated"
                    if(newWorker.state === 'installed') {
                        localStorage.setItem('pwa-version-reload', JSON.stringify(false))
                        localStorage.setItem('pwa-cache-reload', JSON.stringify(true))
                        newWorker.postMessage('SKIP_WAITING');
                    }
                    if(newWorker.state === 'activated') {
                        // Await IndexedDB setup (version, stores)
                        await openIndexedDB()
                        window.location.reload()
                    }
                })
            })
        })
}

// Plugins
import App from "./App.vue";
import Chartist from "chartist";
import VueAxios from "vue-axios";
import DashboardPlugin from "./material-dashboard";
import Swal from "sweetalert2";
import Multiselect from "vue-multiselect";
import VueSignaturePad from 'vue-signature-pad'

// MomentJS
import moment from "moment"
import "moment/locale/fr"
moment.locale('fr')
moment.weekdays(true)

// Vue-Datetime
import { Datetime } from 'vue-datetime'
import 'vue-datetime/dist/vue-datetime.min.css'

// Font-Awesome
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import '@fortawesome/fontawesome-free/css/all.css'
import '@fortawesome/fontawesome-free/js/all.js'

// plugin setup
Vue.use(DashboardPlugin);
Vue.use(VueAxios, axios);
Vue.use(VueSignaturePad)

Vue.prototype.$moment = moment;
Vue.prototype.$swal = Swal;

Vue.component('datetime', Datetime);
Vue.component('multiselect', Multiselect)
Vue.component('font-awesome-icon', FontAwesomeIcon)

// router & store setup
import router from "./router";
import store from "./store";

// Vue Config
Vue.config.errorHandler = async function(err, vm, info) {
    if (vm.$el && vm.$el.baseURI && vm.$el.baseURI.includes('pwa')) {
        let error = JSON.stringify({
            error: err.toString(),
            url: vm.$el.baseURI,
            parentTag: vm.$parent.$vnode.tag,
            componentTag: vm.$vnode.tag,
            info: info,
        })

        let userId = 1
        try {
            userId = vm.$store.state.pwaAuth.technician.id
        } catch (e) {}

        let db = await new Promise((resolve, reject) => {
            let request = window.indexedDB.open(indexedDBName, indexedDBVersion)

            request.onsuccess = event => {
                resolve(event.target.result)
            }
        })

        new Promise(() => {
            const tx = db.transaction('failed_requests', 'readwrite')
            const idbStore = tx.objectStore('failed_requests')
            idbStore.put({ key: vm.$vnode.tag, userId: userId, payload: error, timestamp: moment().toDate().getTime(), error: 'VUEJS errorHandler' })
        })
    }
    throw err;
}

// css import
import "./assets/css/index.css";
import "./assets/scss/main.scss";
import "vue-multiselect/dist/vue-multiselect.min.css";

// global library setup
Vue.prototype.$Chartist = Chartist;

// Session timer
import IdleVue from "idle-vue";

const eventsHub = new Vue();
Vue.use(IdleVue, {
    eventEmitter: eventsHub,
    store,
    idleTime: 900000, // 900000 15 minutes pour passer en état "inactif" et démarer le compteur des 5 heures,
    // startAtIdle: false
});

window.bus = new Vue()

/* eslint-disable no-new */
const app = new Vue({
    router: router,
    store: store,
    el: "#app",
    render: h => h(App)
});

store.$app = app;
